/**
 * Shared types for Reports Management & Delivery
 * Supports saved reports, scheduled delivery, and review workflows
 */
import { z } from "zod";
import { uuidSchema } from "./validation-common.js";
import { HierarchyConfigSchema, PivotFiltersSchema, PivotAggregationMethodSchema, DateAggregationSchema, DerivedSeriesDefinitionSchema, } from "./pivotTable.js";
// ===== ENUMS =====
export const ReportScheduleFrequencySchema = z.enum([
    "DAILY",
    "WEEKLY",
    "BIWEEKLY",
    "MONTHLY",
    "QUARTERLY",
]);
export const ReportReviewStatusSchema = z.enum(["PENDING", "REVIEWED"]);
export const RelativeDatePeriodSchema = z.enum([
    "LAST_7_DAYS",
    "LAST_14_DAYS",
    "LAST_30_DAYS",
    "THIS_WEEK",
    "LAST_WEEK",
    "THIS_MONTH",
    "LAST_MONTH",
    "THIS_QUARTER",
    "LAST_QUARTER",
]);
// ===== PIVOT TABLE PARAMETERS (excludes date range) =====
/**
 * Parameters for saved reports - same as PivotTableRequest but without dates
 * Dates are resolved at delivery time from relativePeriod
 */
export const SavedReportParametersSchema = z.object({
    seriesTypes: z.array(z.string().min(1)).min(1),
    aggregationMethod: PivotAggregationMethodSchema,
    hierarchy: HierarchyConfigSchema,
    filters: PivotFiltersSchema.optional(),
    // Explicit date aggregation (user-selectable) - resolved at delivery time if not set
    dateAggregation: DateAggregationSchema.optional(),
    // Derived series definitions for just-in-time computation (max 10)
    derivedSeries: z.array(DerivedSeriesDefinitionSchema).max(10).optional(),
});
// ===== INPUT SCHEMAS =====
// Conditional formatting schema (flexible structure for FE)
export const ConditionalFormattingRuleSchema = z.object({
    field: z.string(),
    operator: z.string(),
    value: z.union([z.number(), z.string()]),
    style: z.record(z.string(), z.unknown()),
});
export const ConditionalFormattingSchema = z.object({
    rules: z.array(ConditionalFormattingRuleSchema),
});
export const CreateSavedReportInputSchema = z.object({
    name: z.string().min(1).max(255),
    description: z.string().max(2000).optional(),
    category: z.string().max(100).optional(),
    conditionalFormatting: ConditionalFormattingSchema.optional(),
    parameters: SavedReportParametersSchema,
    relativePeriod: RelativeDatePeriodSchema,
});
export const UpdateSavedReportInputSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(2000).optional().nullable(),
    category: z.string().max(100).optional().nullable(),
    conditionalFormatting: ConditionalFormattingSchema.optional().nullable(),
    parameters: SavedReportParametersSchema.optional(),
    relativePeriod: RelativeDatePeriodSchema.optional(),
});
export const CreateScheduleInputSchema = z
    .object({
    recipientEmploymentIds: z.array(uuidSchema).default([]),
    recipientTagIds: z.array(uuidSchema).default([]),
    frequency: ReportScheduleFrequencySchema,
    anchorDate: z.coerce.date().optional(),
})
    .refine((data) => data.recipientEmploymentIds.length > 0 || data.recipientTagIds.length > 0, {
    message: "At least one recipient (employment or tag) is required",
    path: ["recipientEmploymentIds"],
});
export const UpdateScheduleInputSchema = z.object({
    recipientEmploymentIds: z.array(uuidSchema).optional(),
    recipientTagIds: z.array(uuidSchema).optional(),
    frequency: ReportScheduleFrequencySchema.optional(),
    anchorDate: z.coerce.date().optional().nullable(),
    isActive: z.boolean().optional(),
});
export const ReviewInstanceInputSchema = z.object({
    reviewComment: z.string().max(2000).optional(),
});
export const UpdateReviewCommentInputSchema = z.object({
    reviewComment: z.string().max(2000),
});
// ===== ROUTE PARAMETER SCHEMAS =====
export const ReportIdParamSchema = z.object({
    reportId: uuidSchema,
});
export const InstanceIdParamSchema = z.object({
    instanceId: uuidSchema,
});
// Re-export from fileUpload.ts to maintain backwards compatibility
export { CompanyIdQuerySchema } from "./fileUpload.js";
export const ListReportsQuerySchema = z.object({
    companyId: uuidSchema,
    createdByEmploymentId: uuidSchema.optional(),
    includeDeleted: z.coerce.boolean().optional(),
});
export const ListInstancesQuerySchema = z.object({
    companyId: uuidSchema,
    savedReportId: uuidSchema.optional(),
    reviewStatus: ReportReviewStatusSchema.optional(),
    assigneeEmploymentId: uuidSchema.optional(),
    page: z.coerce.number().int().positive().optional().default(1),
    limit: z.coerce.number().int().positive().max(100).optional().default(50),
});
export const MyInstancesQuerySchema = z.object({
    reviewStatus: ReportReviewStatusSchema.optional(),
    savedReportId: uuidSchema.optional(),
});
// ===== VIEWER MANAGEMENT SCHEMAS =====
export const AddViewerInputSchema = z.object({
    employmentId: uuidSchema,
});
export const ViewerEmploymentParamsSchema = z.object({
    reportId: uuidSchema,
    employmentId: uuidSchema,
});
export const AddViewerGroupInputSchema = z.object({
    tagGroupId: uuidSchema,
});
export const ViewerGroupParamsSchema = z.object({
    reportId: uuidSchema,
    tagGroupId: uuidSchema,
});
// ===== COMMENT MANAGEMENT SCHEMAS =====
export const CreateCommentInputSchema = z.object({
    content: z.string().min(1).max(5000),
    isReviewComment: z.boolean().optional(),
});
export const UpdateCommentInputSchema = z.object({
    content: z.string().min(1).max(5000),
});
export const CommentParamsSchema = z.object({
    instanceId: uuidSchema,
    commentId: uuidSchema,
});
